import { Para, Row } from 'ht-sdk/react';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { Heading } from 'ht-sdk/react';
import { Env } from '@/env';
import { Form, PasswordField, resolver, TextField } from '@/components/forms';
import { auth } from '@/auth';
import { TwoFactorSetupWizard } from 'ht-sdk/auth/client';
import { useForm } from 'react-hook-form';
import { LastNameSchema, FirstNameSchema, PasswordSchema } from '@shared/modelTypes/user-types';
import z from 'zod';
import { OnBoardingBottomBar, onboardingClient, OnboardingForm, type OnboardingFormComponent } from 'ht-sdk/onboarding/client';
import { toast } from 'sonner';
import { useRef, useState } from 'react';

const config = onboardingClient({
	apiBaseUrl: Env.VITE_ONBOARDING_SERVER_URL,
});

export const Route = createFileRoute('/onboarding')({
	component: RouteComponent,
});

function RouteComponent() {
	const location = useLocation();
	const searchParams = new URLSearchParams(location.searchStr);
	const token = searchParams.get('token') ?? undefined;

	const navigate = useNavigate();

	return (
		<div className="flex size-full min-h-screen items-center justify-center bg-gray-100">
			<div className="w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
				<OnboardingForm
					fetchFn={auth.fetch}
					token={token}
					config={config}
					FormComponent={FormComponent}
					onComplete={() => {
						navigate({ to: '/login' });
					}}
				/>
			</div>
		</div>
	);
}

// TODO: share type with server
type OnboardingSteps = {
	name: {
		id: 'name';
		name: 'Set Name';
		required: true;
	};
	password: {
		id: 'password';
		name: 'Set Password';
		required: true;
	};
	passkey: {
		id: 'passkey';
		name: 'Register Passkey';
		required: false;
	};
	twoFactor: {
		id: 'twoFactor';
		name: 'Set up two factor authentication';
		required: true;
	};
	termsOfService: {
		id: 'termsOfService';
		name: 'Terms of Service';
		required: true;
		data: {
			version: number;
			content: string;
		};
	};
};

const NameForm: OnboardingFormComponent<OnboardingSteps> = (props) => {
	const form = useForm({ resolver: resolver(z.object({ firstName: FirstNameSchema, lastName: LastNameSchema })) });

	return (
		<Form methods={form} onSubmit={props.onSubmit}>
			<Heading>{props.currentStep.name}</Heading>
			<Para>Set your name.</Para>
			<Row gap={3}>
				<TextField name="firstName" label="First Name" placeholder="First Name" />
				<TextField name="lastName" label="Last Name" placeholder="Last Name" />
			</Row>
			<OnBoardingBottomBar {...props} />
		</Form>
	);
};

const PasswordForm: OnboardingFormComponent<OnboardingSteps> = (props) => {
	const form = useForm({ resolver: resolver(z.object({ password: PasswordSchema })) });

	return (
		<Form
			methods={form}
			onSubmit={async (data) => {
				const res = await auth.api.passwordReset.setPassword({
					registrationToken: props.token!,
					password: data.password,
				});
				if (!res.success) {
					toast(res.message);
					return;
				}
				props.onSubmit({});
			}}
		>
			<Heading>{props.currentStep.name}</Heading>
			<Para>Set a password for your account.</Para>
			<PasswordField name="password" label="Password" placeholder="Password" />
			<OnBoardingBottomBar {...props} />
		</Form>
	);
};

const PasskeyForm: OnboardingFormComponent<OnboardingSteps> = (props) => {
	return (
		<form
			className="flex flex-col items-center gap-2"
			onSubmit={async (e) => {
				e.preventDefault();
				const res = await auth.registerPasskey({ registrationToken: props.token });
				if (!res.success) {
					toast.error(res.message);
					return;
				}
				toast.success('Passkey registered successfully');
				props.onSubmit({});
			}}
		>
			<Heading>{props.currentStep.name}</Heading>
			<Para>Setup a passkey to use for authentication.</Para>
			<OnBoardingBottomBar {...props} />
		</form>
	);
};

const TermsOfServiceForm: OnboardingFormComponent<OnboardingSteps> = (props) => {
	const form = useForm({});
	const scrollRef = useRef<HTMLDivElement>(null);
	const [fadeStopPercent, setFadeStopPercent] = useState(60);

	const step = props.currentStep as OnboardingSteps['termsOfService'];

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		const maxScroll = scrollHeight - clientHeight;
		const scrollRatio = scrollTop / maxScroll;
		const newFadeStop = 60 + 40 * scrollRatio;
		setFadeStopPercent(newFadeStop);
	};

	const gradient = `linear-gradient(to bottom, black ${fadeStopPercent}%, transparent 100%)`;

	return (
		<Form
			methods={form}
			onSubmit={async () => {
				props.onSubmit({});
			}}
		>
			<Heading>{step.name}</Heading>
			<Para>Accept the terms of service.</Para>

			<div className="mt-6 max-h-96 w-full overflow-hidden rounded-2xl border border-gray-200 px-8 py-6 text-left text-sm leading-5 text-gray-700">
				<div
					ref={scrollRef}
					className="white max-h-60 overflow-y-auto whitespace-pre-line"
					style={{
						maskImage: gradient,
						WebkitMaskImage: gradient,
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						transition: 'mask-image 0.3s ease, -webkit-mask-image 0.3s ease',
					}}
					onScroll={handleScroll}
				>
					{step.data.content}
				</div>
			</div>
			<div className="flex justify-center">
				<OnBoardingBottomBar {...props} />
			</div>
		</Form>
	);
};

const FormComponent: OnboardingFormComponent<OnboardingSteps> = (props) => {
	const stepId = props.currentStep.id;

	if (stepId === 'name') {
		return <NameForm {...props} />;
	}

	if (stepId === 'password') {
		return <PasswordForm {...props} />;
	}

	if (stepId === 'passkey') {
		return <PasskeyForm {...props} />;
	}

	if (stepId === 'twoFactor') {
		console.log('two factor setup', props);

		return (
			<TwoFactorSetupWizard
				auth={auth}
				registrationToken={props.token}
				onCancel={props.currentStep.required ? undefined : props.onSkip}
				onConfirm={async () => {
					if (props.token) {
						// TODO: maybe move this elsewhere instead of hardcoded after 2fa
						const res = await auth.api.finalizeRegistration({ registrationToken: props.token! });
						if (!res.success) {
							toast(res.message);
							return;
						}
					}
					return props.onSubmit({});
				}}
			/>
		);
	}
	if (stepId === 'termsOfService') {
		return <TermsOfServiceForm {...props} />;
	}
};
